import type { EnemySpriteId } from "./enemies";

export const VIEW_WIDTH = 360;
export const VIEW_HEIGHT = 640;
export const FIXED_DT = 1 / 60;
export const CHAMBER_HEIGHT = 260;
export const WALL_LEFT = 24;
export const WALL_RIGHT = 336;
export const PULP_RADIUS = 20;
export const CORE_RADIUS = 14.4;
export const RULESET_VERSION = "patent-drop-0.16.0-rapier-0.20.0";
export const MAX_PATENTS = 3;
export const REGULAR_PATENT_CHAMBERS = 12;
export const GOLD_PATENT_CHAMBERS = 36;
export const PATENT_DAMAGE_INVULNERABILITY_TICKS = 45;
export const PATENT_MAX_BONUS_SCORE = 500;

export const PHYSICS = {
  gravity: 960,
  pulpTerminalSpeed: 220,
  coreTerminalSpeed: 520,
  pulpRestitution: 0.08,
  coreRestitution: 0.6,
  pulpFriction: 0.18,
  coreFriction: 0.02,
} as const;

export type Material = "pulp" | "core";
export type RunState = "title" | "playing" | "paused" | "dead";
export type RunMode = "normal" | "random";
export type HazardKind = "teeth" | "press" | "gear" | "pendulum" | "gate";
export type EnemyMotion = "static" | "horizontal" | "vertical" | "burst" | "adaptive";
export type PowerupKind = "regular-patent" | "gold-patent";

export interface EraSpec {
  id: string;
  year: string;
  name: string;
  paper: string;
  ink: string;
  accent: string;
  produceWeight: number;
  formationChance: number;
  enemyMotionWeights: readonly [number, number, number, number, number];
  movingPlatformChance: number;
  platformTravel: readonly [number, number];
  platformPeriod: number;
  spacingJitter: number;
  motionPace: number;
}

export interface SegmentSpec {
  id: string;
  chamber: number;
  ax: number;
  ay: number;
  bx: number;
  by: number;
  phase?: number;
  travelX?: number;
  travelY?: number;
  period?: number;
}

export type DeathCause = "incompatible-machine" | "incompatible-produce" | "time-collapse" | "timeline-corruption" | "unknown";

export interface HazardSpec {
  id: string;
  chamber: number;
  kind: HazardKind;
  weakness: Material;
  sprite: EnemySpriteId;
  x: number;
  y: number;
  width: number;
  height: number;
  phase?: number;
  motion: EnemyMotion;
  travelX?: number;
  travelY?: number;
  period?: number;
  reactivity?: number;
}

export interface PowerupSpec {
  id: string;
  chamber: number;
  kind: PowerupKind;
  x: number;
  y: number;
  radius: number;
}

export interface ChamberSpec {
  index: number;
  label: string;
  era: EraSpec;
  segments: SegmentSpec[];
  hazards: HazardSpec[];
  powerups: PowerupSpec[];
}

export interface ScoreState {
  score: number;
  maxDepth: number;
  nextDepthSlice: number;
  combo: number;
}

export type GameEvent =
  | { type: "material"; material: Material; blocked: boolean }
  | { type: "impact"; material: Material; speed: number; x: number; y: number }
  | { type: "milestone"; label: string }
  | { type: "piston"; x: number; y: number; normalX: number; normalY: number }
  | { type: "hazardBreak"; x: number; y: number; combo: number; points: number }
  | { type: "patentCollected"; x: number; y: number; count: number; added: boolean }
  | { type: "patentLost"; x: number; y: number; directionX: number; count: number }
  | { type: "overdrive"; x: number; y: number }
  | { type: "death"; cause: DeathCause; reason: string; x: number; y: number };

export interface RenderSegment extends SegmentSpec {
  offsetX: number;
  offsetY: number;
}

export interface RenderHazard extends HazardSpec {
  renderX: number;
  renderY: number;
}

export interface RenderPowerup extends PowerupSpec {
  collected: boolean;
}

export interface GameSnapshot {
  state: RunState;
  mode: RunMode;
  tick: number;
  seed: number;
  eraSeed: number;
  era: EraSpec;
  tomato: {
    x: number;
    y: number;
    vx: number;
    vy: number;
    radius: number;
    material: Material;
    blockedExpansion: boolean;
  };
  cameraY: number;
  sterilizerY: number;
  pace: number;
  overdriveTicks: number;
  patentCount: number;
  scoreMultiplier: number;
  score: number;
  depth: number;
  combo: number;
  chamberLabel: string;
  segments: RenderSegment[];
  hazards: RenderHazard[];
  powerups: RenderPowerup[];
}
